import {
  FloorProgressTranslation,
  ProgressTranslationAffinePayload,
} from '../api/types'

export type FloorProgressTransform = readonly [
  number,
  number,
  number,
  number,
  number,
  number,
]

export function parseAffine(
  payload: ProgressTranslationAffinePayload | string | null | undefined,
): FloorProgressTransform | null {
  if (payload == null) return null

  let obj: ProgressTranslationAffinePayload
  if (typeof payload === 'string') {
    try {
      obj = JSON.parse(payload) as ProgressTranslationAffinePayload
    } catch {
      return null
    }
  } else {
    obj = payload
  }

  const linear = obj.linear_transformation
  const translation = obj.translation
  if (
    !linear ||
    linear.length !== 2 ||
    !linear[0] ||
    !linear[1] ||
    linear[0].length !== 2 ||
    linear[1].length !== 2 ||
    !translation ||
    translation.length !== 2
  ) {
    return null
  }

  const a = Number(linear[0][0])
  const b = Number(linear[0][1])
  const c = Number(linear[1][0])
  const d = Number(linear[1][1])
  const tx = Number(translation[0])
  const ty = Number(translation[1])

  if (![a, b, c, d, tx, ty].every(Number.isFinite)) {
    return null
  }

  const det = a * d - b * c
  if (Math.abs(det) < 1e-12) {
    return null
  }

  return [a, b, c, d, tx, ty] as const
}

export function applyAffine(
  m: FloorProgressTransform,
  px: readonly [number, number],
): [number, number] {
  const [a, b, c, d, tx, ty] = m
  const [x, y] = px
  return [a * x + b * y + tx, c * x + d * y + ty]
}

/** Inverse of plan pixels → world XY (for STL vertex → 2D hit-test). */
export function worldToPlan(
  m: FloorProgressTransform,
  wx: number,
  wy: number,
): [number, number] {
  const [a, b, c, d, tx, ty] = m
  const det = a * d - b * c
  const ox = wx - tx
  const oy = wy - ty
  return [(d * ox - b * oy) / det, (-c * ox + a * oy) / det]
}

export function getPrimaryToSecondary(
  translation: FloorProgressTranslation | null | undefined,
): FloorProgressTransform | null {
  if (!translation) return null
  return parseAffine(translation.primary_to_secondary)
}

function parsePrimaryPayload(
  translation: FloorProgressTranslation | null | undefined,
): ProgressTranslationAffinePayload | null {
  if (!translation) return null
  const payload = translation.primary_to_secondary
  if (!payload) return null

  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload) as ProgressTranslationAffinePayload
    } catch {
      return null
    }
  }
  return payload
}

export function getFloorBaseZ(
  translation: FloorProgressTranslation | null | undefined,
): number | null {
  const obj = parsePrimaryPayload(translation)
  if (!obj) return null
  const z = obj.floor_height
  return typeof z === 'number' && Number.isFinite(z) ? z : null
}

/** Robot eye height in CAD/world Z (inference Blender camera Z). */
export function getCadHeight(
  translation: FloorProgressTranslation | null | undefined,
): number | null {
  const obj = parsePrimaryPayload(translation)
  if (!obj) return null
  const z = Number(obj.cad_height)
  return Number.isFinite(z) ? z : null
}

/**
 * Extra yaw calibration degrees from progress_translation (Blender:
 * view_angle = -(image_angle + rotation)).
 */
export function getProgressRotation(
  translation: FloorProgressTranslation | null | undefined,
): number {
  const obj = parsePrimaryPayload(translation)
  if (!obj) return 0
  const r = Number(obj.rotation)
  return Number.isFinite(r) ? r : 0
}
